/* eslint-disable sonarjs/cognitive-complexity */
import * as Headless from '@headlessui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { differenceInCalendarDays } from 'date-fns';
import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import type {
  Document,
  PaginatedDocumentList,
  PaginatedDocumentTypeList,
  PatchedPatchedDocument,
} from '../../../js/api';
import { DocumentsService, DocumentStatusEnum, OwnerTypeEnum } from '../../../js/api';
import { handleApiError } from '../../../js/app/utils/errorhandling';
import { useIsMobile } from '../hooks/useIsMobile';
import {
  formatApiDate,
  formatDateTo24Hr,
  getDateTemporalityClass,
  parseApiLocalDate,
} from '../utils/date';
import { useAudience } from '../utils/workflowAccess';
import { Button } from './button';
import { ConfirmationDialog } from './confirmation-dialog';
import { DocumentEditDialog } from './document-edit-dialog';
import { DocumentReplaceDialog } from './document-replace-dialog';
import { DocumentUploadDialog } from './document-upload-dialog';
import { Dropdown, DropdownButton, DropdownItem, DropdownMenu } from './dropdown';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './table';
import { TooltippedText } from './tooltipped-text';

export const DOCUMENTS_QUERY_KEY = 'documents';
export const DOCUMENT_TYPES_QUERY_KEY = 'documentTypes';

type DocumentsTabProps = {
  /** E.g. 'connections.connectedfacility', 'connections.clearance' */
  contextType: string;
  /** The entity ID to scope documents to */
  contextId: string;
  /** Who owns uploaded documents — typically the logged-in user's entity ID */
  ownerId: string;
  /** Optional provider ID for HCO-owned docs about a specific provider */
  relatedProviderId?: string;
};

type StatusIndicator = {
  iconClassName: string;
  colorClassName: string;
  bgColorClassName: string;
  ariaLabel: string;
  tooltip: string;
};

function getStatusIndicator(
  status: Document['status'] | string | null | undefined
): StatusIndicator | null {
  switch (status) {
    case DocumentStatusEnum.PENDING:
      return {
        iconClassName: 'fas fa-exclamation-triangle',
        colorClassName: 'tw:text-stoplight-warning',
        bgColorClassName: 'tw:bg-stoplight-warning',
        ariaLabel: 'Pending',
        tooltip: 'Upload did not complete',
      };
    case DocumentStatusEnum.FAILED:
      return {
        iconClassName: 'fas fa-triangle-exclamation',
        colorClassName: 'tw:text-stoplight-danger',
        bgColorClassName: 'tw:bg-stoplight-danger',
        ariaLabel: 'Failed',
        tooltip: 'Upload failed',
      };
    case DocumentStatusEnum.PROCESSING:
      return {
        iconClassName: 'fas fa-circle-info',
        colorClassName: 'tw:text-blue-800',
        bgColorClassName: 'tw:bg-blue-800',
        ariaLabel: 'Processing',
        tooltip: 'Processing, check back later',
      };
    default:
      return null;
  }
}

function formatExpiresIn(expiresOn: string | null | undefined): string | null {
  const date = parseApiLocalDate(expiresOn);
  if (!date) return null;
  const days = differenceInCalendarDays(date, new Date());
  if (days < -1) return `${Math.abs(days)} days ago`;
  if (days === -1) return 'yesterday';
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  return `in ${days} days`;
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '-';
  const formatted = formatDateTo24Hr(value);
  return formatted || '-';
}

export function DocumentsTab({
  contextType,
  contextId,
  ownerId,
  relatedProviderId,
}: DocumentsTabProps) {
  const queryClient = useQueryClient();
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [docPendingDelete, setDocPendingDelete] = useState<Document | null>(null);
  const [docEditing, setDocEditing] = useState<Document | null>(null);
  const [docPendingReplace, setDocPendingReplace] = useState<Document | null>(null);

  const audience = useAudience();
  const isMobile = useIsMobile();

  // Fetch documents for this context
  const { data: documents, isLoading: isLoadingDocuments } = useQuery({
    queryKey: [DOCUMENTS_QUERY_KEY, contextId],
    queryFn: () =>
      DocumentsService.documentsList({
        contextType,
        contextId,
      }),
    enabled: !!contextId,
    select: (data: PaginatedDocumentList) => data?.results ?? [],
  });

  // Fetch document types relevant to this context. The backend scopes to
  // types the user's audience has any permission on; we derive the
  // uploadable subset client-side from the audience arrays.
  const { data: documentTypes } = useQuery({
    queryKey: [DOCUMENT_TYPES_QUERY_KEY, contextType, audience],
    queryFn: () =>
      DocumentsService.documentsTypesList({
        contextTypes: [contextType],
      }),
    select: (data: PaginatedDocumentTypeList) => data?.results ?? [],
  });

  const uploadableTypes = useMemo(
    () =>
      (documentTypes ?? []).filter((dt) => (dt.allowed_audience_upload ?? []).includes(audience)),
    [documentTypes, audience]
  );

  const invalidateDocuments = () =>
    queryClient.invalidateQueries({ queryKey: [DOCUMENTS_QUERY_KEY, contextId] });

  const patchDocumentMutation = useMutation({
    mutationFn: async (input: { id: string; requestBody: PatchedPatchedDocument }) =>
      DocumentsService.documentsPartialUpdate(input),
    onSuccess: () => {
      toast.success('Document updated');
      invalidateDocuments();
    },
    onError: (error: Error) => {
      handleApiError(error, { messagePrefix: 'Failed to update document', handle403: true });
    },
  });

  const downloadMutation = useMutation({
    mutationFn: async ({ id, disposition = 'inline' }: { id: string; disposition?: string }) =>
      DocumentsService.documentsDownloadRetrieve({ id, disposition }),
    onSuccess: (data) => {
      if (data?.url) {
        window.open(data.url, '_blank', 'noopener,noreferrer');
      } else {
        toast.error('Download URL not available');
      }
    },
    onError: (error: Error) => {
      handleApiError(error, { messagePrefix: 'Failed to download document', handle403: true });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => DocumentsService.documentsDestroy({ id }),
    onSuccess: () => {
      toast.success('Document removed');
      setDocPendingDelete(null);
      invalidateDocuments();
    },
    onError: (error: Error) => {
      handleApiError(error, { messagePrefix: 'Failed to remove document', handle403: true });
    },
  });

  const confirmDelete = async () => {
    if (!docPendingDelete) return;
    try {
      await deleteMutation.mutateAsync(docPendingDelete.id);
    } catch {
      // Toast already shown by deleteMutation.onError
    } finally {
      setDocPendingDelete(null);
    }
  };

  // Submit handler for the edit dialog (label + dates). Closes the dialog on
  // settle; the mutation's onError handles the toast.
  const handleEditSubmit = async (payload: PatchedPatchedDocument) => {
    if (!docEditing) return;
    try {
      await patchDocumentMutation.mutateAsync({
        id: docEditing.id,
        requestBody: payload,
      });
    } catch {
      // Toast already shown by patchDocumentMutation.onError
    } finally {
      setDocEditing(null);
    }
  };

  const hasUploadableTypes = (uploadableTypes?.length ?? 0) > 0;
  const canRenderUploadDialog = !!ownerId;
  const tableCellStyles = 'tw:!p-1';
  // Hide the status icon column entirely when no row needs an indicator,
  // so the table reclaims that horizontal space.
  const showStatusColumn = (documents ?? []).some(
    (doc: Document) => getStatusIndicator(doc.status) !== null
  );

  return (
    <div className="tw:space-y-4 tw:mb-2">
      <div className="tw:flex tw:items-center tw:justify-between">
        {hasUploadableTypes && canRenderUploadDialog && (
          <Button
            color="brand-green-outline"
            small
            onClick={() => setIsUploadDialogOpen(true)}
            data-testid="connection-documents-upload-button"
          >
            <i className="fas fa-upload" />
            Upload Document
          </Button>
        )}
      </div>

      {isLoadingDocuments ? (
        <div className="tw:text-sm tw:text-zinc-500">Loading documents...</div>
      ) : !documents || documents.length === 0 ? (
        <div className="tw:rounded-md tw:border tw:border-dashed tw:border-zinc-300 tw:dark:border-zinc-700 tw:p-6 tw:text-center tw:text-sm tw:text-zinc-500">
          No documents have been uploaded for this connection yet.
        </div>
      ) : (
        <Table dense>
          <TableHead>
            <TableRow className="tw:hover:bg-transparent" noPointer>
              <TableHeader className={clsx(tableCellStyles, 'tw:w-px')}>Action</TableHeader>
              {showStatusColumn && <TableHeader className={clsx(tableCellStyles, 'tw:w-6')} />}
              <TableHeader className={clsx(tableCellStyles, 'tw:!pl-2 tw:w-px')}>Name</TableHeader>
              <TableHeader className={clsx(tableCellStyles, 'tw:w-px tw:!px-2')}>Type</TableHeader>
              <TableHeader className={clsx(tableCellStyles, 'tw:w-px')}>Uploaded</TableHeader>
              <TableHeader className={tableCellStyles}>Expiration</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {documents.map((doc: Document) => {
              const isConfirmed = doc.status === DocumentStatusEnum.CONFIRMED;
              const isOrphaned =
                doc.status === DocumentStatusEnum.PENDING ||
                doc.status === DocumentStatusEnum.FAILED;
              const isOwner = doc.owner_id === ownerId;
              const canEdit = isOwner; // partial_update (label, dates) requires ownership
              const canDelete =
                isOrphaned ||
                (isOwner && (doc.document_type?.allowed_audience_delete ?? []).includes(audience));
              const canReplace =
                isConfirmed &&
                (doc.document_type?.allowed_audience_replace ?? []).includes(audience);
              const statusIndicator = getStatusIndicator(doc.status);
              const expiresInLabel = formatExpiresIn(doc.expires_on);
              const expiresColorClass = getDateTemporalityClass(doc.expires_on);

              return (
                <TableRow key={doc.id} className="" noPointer>
                  <TableCell className={clsx(tableCellStyles, 'tw:w-px')}>
                    {isMobile ? (
                      <Dropdown>
                        <DropdownButton
                          as="button"
                          className="tw:inline-flex tw:items-center tw:justify-center tw:rounded-md
                          tw:p-1.5 tw:text-zinc-500 tw:hover:bg-zinc-200 tw:hover:text-zinc-700
                          tw:focus:outline-none tw:focus:ring-2 tw:focus:ring-brand-green-0"
                          aria-label="Actions"
                          data-testid={`document-actions-menu-${doc.id}`}
                        >
                          <i className="fas fa-fw fa-ellipsis-vertical" />
                        </DropdownButton>
                        <Headless.Portal>
                          <DropdownMenu anchor="bottom end" className="tw:z-[70]">
                            {isConfirmed && (
                              <>
                                <DropdownItem
                                  onClick={() =>
                                    downloadMutation.mutate({ id: doc.id, disposition: 'inline' })
                                  }
                                >
                                  <i data-slot="icon" className="fas fa-fw fa-eye" />
                                  View
                                </DropdownItem>
                                <DropdownItem
                                  onClick={() =>
                                    downloadMutation.mutate({
                                      id: doc.id,
                                      disposition: 'attachment',
                                    })
                                  }
                                >
                                  <i data-slot="icon" className="fas fa-fw fa-download" />
                                  Download
                                </DropdownItem>
                              </>
                            )}
                            {canEdit && (
                              <DropdownItem onClick={() => setDocEditing(doc)}>
                                <i data-slot="icon" className="fas fa-fw fa-pencil" />
                                Edit
                              </DropdownItem>
                            )}
                            {canReplace && (
                              <DropdownItem onClick={() => setDocPendingReplace(doc)}>
                                <i data-slot="icon" className="fas fa-fw fa-arrows-rotate" />
                                Replace
                              </DropdownItem>
                            )}
                            {canDelete && (
                              <DropdownItem onClick={() => setDocPendingDelete(doc)}>
                                <i
                                  data-slot="icon"
                                  className="fas fa-fw fa-trash-can tw:!text-stoplight-danger"
                                />
                                <span className="tw:text-stoplight-danger">Delete</span>
                              </DropdownItem>
                            )}
                          </DropdownMenu>
                        </Headless.Portal>
                      </Dropdown>
                    ) : (
                      <span
                        className="tw:flex tw:items-center tw:justify-start tw:gap-1"
                        data-testid="document-actions"
                      >
                        {isConfirmed && (
                          <>
                            <Button
                              color="brand-green-outline"
                              small
                              title="View Document"
                              onClick={() =>
                                downloadMutation.mutate({ id: doc.id, disposition: 'inline' })
                              }
                              disabled={downloadMutation.isPending}
                              aria-label={`View ${doc.label || 'document'}`}
                              data-testid={`document-view-${doc.id}`}
                            >
                              <i className="fas fa-fw fa-eye" />
                            </Button>
                            <Button
                              color="brand-green-outline"
                              small
                              title="Download Document"
                              onClick={() =>
                                downloadMutation.mutate({ id: doc.id, disposition: 'attachment' })
                              }
                              disabled={downloadMutation.isPending}
                              aria-label={`Download ${doc.label || 'document'}`}
                              data-testid={`document-download-${doc.id}`}
                            >
                              <i className="fas fa-fw fa-download" />
                            </Button>
                          </>
                        )}
                        {canEdit && (
                          <Button
                            color="brand-green-outline"
                            small
                            title="Edit document"
                            onClick={() => setDocEditing(doc)}
                            disabled={patchDocumentMutation.isPending}
                            aria-label={`Edit ${doc.label || 'document'}`}
                            data-testid={`document-edit-${doc.id}`}
                          >
                            <i className="fas fa-fw fa-pencil" />
                          </Button>
                        )}
                        {canReplace && (
                          <Button
                            color="brand-green-outline"
                            small
                            title="Replace document"
                            onClick={() => setDocPendingReplace(doc)}
                            disabled={false}
                            aria-label={`Replace ${doc.label || 'document'}`}
                            data-testid={`document-replace-${doc.id}`}
                          >
                            <i className="fas fa-fw fa-arrows-rotate" />
                          </Button>
                        )}
                        {canDelete && (
                          <Button
                            color="stoplight-danger-outline"
                            small
                            title="Delete document"
                            onClick={() => setDocPendingDelete(doc)}
                            disabled={deleteMutation.isPending}
                            aria-label={`Delete ${doc.label || 'document'}`}
                            data-testid={`document-delete-${doc.id}`}
                          >
                            <i className="fas fa-fw fa-trash-can" />
                          </Button>
                        )}
                      </span>
                    )}
                  </TableCell>

                  {showStatusColumn && (
                    <TableCell className={clsx(tableCellStyles, 'tw:w-6 tw:text-center')}>
                      {statusIndicator && (
                        <TooltippedText
                          tooltipContent={statusIndicator.tooltip}
                          tooltipClassName={statusIndicator.bgColorClassName}
                        >
                          <i
                            className={`${statusIndicator.iconClassName} ${statusIndicator.colorClassName}`}
                            aria-label={statusIndicator.ariaLabel}
                          />
                        </TooltippedText>
                      )}
                    </TableCell>
                  )}
                  <TableCell className={clsx(tableCellStyles, 'tw:cursor-pointer tw:w-px')}>
                    <TooltippedText
                      tooltipContent={
                        <>
                          Original Filename:
                          <br />
                          {doc.original_filename}
                        </>
                      }
                    >
                      {doc.label || doc.original_filename}
                    </TooltippedText>
                  </TableCell>
                  <TableCell className={clsx(tableCellStyles, 'tw:cursor-pointer tw:!px-2')}>
                    <TooltippedText tooltipContent={doc.document_type.title}>
                      {doc.document_type.code}
                    </TooltippedText>
                  </TableCell>
                  <TableCell className={clsx(tableCellStyles, 'tw:cursor-pointer')}>
                    <TooltippedText tooltipContent={`By ${doc.created_by_name}`}>
                      {formatDate(doc.uploaded_at)}
                    </TooltippedText>
                  </TableCell>
                  <TableCell className={tableCellStyles}>
                    {expiresInLabel ? (
                      <TooltippedText
                        tooltipContent={
                          <div className="tw:flex tw:flex-col tw:gap-0.5">
                            <span>
                              Issued:{' '}
                              {formatApiDate(doc.issued_on, { emptyLabel: 'Not specified' })}
                            </span>
                            <span>Expires: {formatApiDate(doc.expires_on)}</span>
                          </div>
                        }
                      >
                        <span className={expiresColorClass}>{expiresInLabel}</span>
                      </TooltippedText>
                    ) : (
                      <span className="tw:text-zinc-400" />
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {canRenderUploadDialog && (
        <DocumentUploadDialog
          open={isUploadDialogOpen}
          onClose={() => setIsUploadDialogOpen(false)}
          onSuccess={() => {
            invalidateDocuments();
          }}
          ownerType={audience as OwnerTypeEnum}
          ownerId={ownerId}
          relatedProviderId={relatedProviderId}
          contextType={contextType}
          contextId={contextId}
        />
      )}

      <ConfirmationDialog
        open={!!docPendingDelete}
        onClose={() => {
          if (!deleteMutation.isPending) setDocPendingDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Delete document?"
        body={
          <p className="tw:text-zinc-700 tw:dark:text-zinc-300">
            Are you sure you want to delete{' '}
            <span className="tw:font-semibold">
              {docPendingDelete?.label || docPendingDelete?.original_filename || 'this document'}
            </span>
            ?
          </p>
        }
        confirmText="Delete"
        confirmVariant="danger"
        isLoading={deleteMutation.isPending}
      />

      <DocumentEditDialog
        open={!!docEditing}
        doc={docEditing}
        onClose={() => {
          if (!patchDocumentMutation.isPending) setDocEditing(null);
        }}
        onSubmit={handleEditSubmit}
        isSubmitting={patchDocumentMutation.isPending}
      />

      <DocumentReplaceDialog
        open={!!docPendingReplace}
        doc={docPendingReplace}
        onClose={() => setDocPendingReplace(null)}
        onSuccess={() => invalidateDocuments()}
        isExpirable={!!docPendingReplace?.document_type?.is_expirable}
      />
    </div>
  );
}
